import type { Component } from 'vue'
import {
  BookIcon,
  CalendarMinimalisticIcon,
  ChatRoundLineIcon,
  DocumentTextIcon,
  FileTextIcon,
  GalleryIcon,
  InboxIcon,
  PaletteIcon,
  PenIcon,
  RadarIcon,
  RoutingIcon,
  UsersGroupRoundedIcon,
} from '@solar-icons/vue/linear'

/** A row that navigates to `to` and lights up for every route that belongs to its place. */
export interface NavLink {
  id: string
  /** i18n key for the nav label, resolved by the caller with `t()`. */
  labelKey: string
  icon: Component
  to: string
  /** Path prefixes that activate this row - `to` itself plus every route this place owns. */
  match: readonly string[]
}

/** A run of rows, optionally introduced by a label. The first, unlabelled group has none. */
export interface NavGroup {
  id: string
  /** i18n key for the group label, resolved by the caller with `t()`. Omitted for the first group. */
  labelKey?: string
  rows: readonly NavLink[]
}

export const NAV_ITEMS: readonly NavGroup[] = [
  {
    id: 'main',
    rows: [
      {
        id: 'home',
        labelKey: 'nav.chat',
        icon: ChatRoundLineIcon,
        to: '/',
        match: ['/', '/chats', '/chat'],
      },
      { id: 'inbox', labelKey: 'nav.inbox', icon: InboxIcon, to: '/inbox', match: ['/inbox'] },
      {
        id: 'calendar',
        labelKey: 'nav.calendar',
        icon: CalendarMinimalisticIcon,
        to: '/calendar',
        match: ['/calendar'],
      },
      {
        id: 'visibility',
        labelKey: 'nav.visibility',
        icon: RadarIcon,
        to: '/visibility',
        match: ['/visibility'],
      },
    ],
  },
  {
    id: 'content',
    labelKey: 'nav.content',
    rows: [
      {
        id: 'pages',
        labelKey: 'nav.pages',
        icon: DocumentTextIcon,
        to: '/pages',
        match: ['/pages'],
      },
      {
        id: 'posts',
        labelKey: 'nav.posts',
        icon: PenIcon,
        to: '/posts',
        match: ['/posts'],
      },
      {
        id: 'strategy',
        labelKey: 'nav.strategy',
        icon: RoutingIcon,
        to: '/strategy',
        match: ['/strategy'],
      },
      {
        id: 'media',
        labelKey: 'nav.media',
        icon: GalleryIcon,
        to: '/media/uploads',
        match: ['/media', '/designs'],
      },
    ],
  },
  {
    id: 'brand',
    labelKey: 'nav.brand',
    rows: [
      {
        id: 'identity',
        labelKey: 'nav.profile',
        icon: BookIcon,
        to: '/identity/brand-facts',
        match: ['/identity'],
      },
      {
        id: 'style',
        labelKey: 'nav.style',
        icon: PaletteIcon,
        to: '/style/voice',
        match: ['/style'],
      },
      {
        id: 'competitors',
        labelKey: 'nav.competitors',
        icon: UsersGroupRoundedIcon,
        to: '/competitors',
        match: ['/competitors'],
      },
      {
        id: 'documents',
        labelKey: 'nav.knowledge',
        icon: FileTextIcon,
        to: '/documents',
        match: ['/documents'],
      },
    ],
  },
]

/** True when `path` belongs to `link`'s place: an exact match, or a `/`-bounded descendant. */
function isNavLinkActive(link: NavLink, path: string): boolean {
  return link.match.some((prefix) =>
    prefix === '/' ? path === '/' : path === prefix || path.startsWith(`${prefix}/`),
  )
}

/** The row lit for `path`, or `undefined` when no place claims it (e.g. Settings). */
export function findActiveNavLink(path: string): NavLink | undefined {
  for (const group of NAV_ITEMS) {
    const row = group.rows.find((link) => isNavLinkActive(link, path))
    if (row) return row
  }
  return undefined
}
